import { isAreaSection } from "@/components/ui/tree/capture-tree/capture-tree-discriminator";
import { selectVideoModeCopyTag } from "@/components/ui/tree/tree-context";
import { IElementsSubscriptionsFeatures } from "@/modes/mode";
import {
  CurrentScene,
  selectDataSet360s,
  selectIsElementViewablePointCloudStream,
} from "@/modes/mode-selectors";
import { CurrentAreaData } from "@/modes/select-current-area-data";
import {
  OnlyFeaturesState,
  selectFeaturesSlice,
} from "@/store/features/features-slice";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { selectClosestElementToPosition } from "@/store/selections-selectors";
import { selectCanReadPointCloud } from "@/store/subscription-features/subscription-features-selectors";
import {
  IElementOrthomosaic,
  IElementPhotogrammetryScan,
  isIElementOrthomosaic,
  isIElementPhotogrammetryScan,
} from "@/types/ielement-extra-types";
import { SceneFilter } from "@/types/scene-filter";
import { isIElementViewableGaussianSplats } from "@/types/type-guards";
import {
  IElementsState,
  selectAncestor,
  selectChildDepthFirst,
  selectChildrenDepthFirst,
  selectIElement,
  selectIsDataSessionAligned,
  selectIsFloorWithoutScale,
  selectIsPointCloudAligned,
  State,
} from "@faro-lotv/app-component-toolbox";
import {
  IElement,
  IElementDepthMap,
  IElementGaussianSplats,
  IElementImg360,
  IElementPointCloudStream,
  IElementType,
  isIElementDepthMap,
  isIElementGaussianSplats,
  isIElementGenericDataSession,
  isIElementGenericPointCloudStream,
  isIElementImg2d,
  isIElementImg360,
  isIElementPointCloudStream,
  isIElementRoomSection,
  isIElementSectionDataSession,
  isIElementSectionDataSetCapture,
  isIElementSectionRoviScan,
  isIElementTimeTravelSection,
  isValid,
  pickClosestInTime,
} from "@faro-lotv/ielement-types";
import { createSelector } from "@reduxjs/toolkit";
import { Vector3 } from "three";
import { WalkSceneActiveElement } from "./walk-types";

/**
 * Selects the point cloud stream for the current scene and area.
 * If the active element is a point cloud stream, it returns it.
 * Otherwise, it picks the point cloud inside the ancestor dataset,
 * falling back to the closest point cloud stream in time
 * from the reference element, if no ancestor dataset exists.
 *
 * @param state The state of the app store
 * @param scene The current active scene
 * @param area The data available in the current area
 * @returns The selected point cloud stream or undefined if not found
 */
export const selectComputePointcloud = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, scene: CurrentScene) => scene,
      (state: OnlyIElementsStore, scene: CurrentScene, area: CurrentAreaData) =>
        area,
    ],
    computePointCloud,
  ),
);

function computePointCloud(
  iElements: IElementsState,
  scene: CurrentScene,
  area: CurrentAreaData,
): IElementPointCloudStream | undefined {
  if (isIElementPointCloudStream(scene.activeElement)) {
    return scene.activeElement;
  }

  const reference = scene.referenceElement ?? area.dataSessions.at(0);
  if (!reference) {
    return undefined;
  }

  const state = { iElements };
  const sections = [...area.dataSessions3d.pointClouds, ...area.dataSessions5d];
  const sectionIds = new Set(sections.map((s) => s.id));
  // Prefer the section the user is actually in (matching the dropdown), falling back to closest in time.
  const section =
    selectAncestor(reference, (el) => sectionIds.has(el.id))(state) ??
    pickClosestInTime(reference, sections);
  return section
    ? selectChildDepthFirst(section, (el): el is IElementPointCloudStream =>
        selectIsElementViewablePointCloudStream(el)(state),
      )(state)
    : undefined;
}

/**
 * Selects the Gaussian splats for the current scene and area.
 * If the active element is Gaussian splats, it returns it.
 * Otherwise, it finds the closest Gaussian splats in time from the reference element.
 *
 * @param state The state of the app store
 * @param scene The current active scene
 * @param area The data available in the current area
 * @returns The selected Gaussian splats or undefined if not found
 */
export const selectComputeGaussianSplats = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, scene: CurrentScene) => scene,
      (state: OnlyIElementsStore, scene: CurrentScene, area: CurrentAreaData) =>
        area,
    ],
    computeGaussianSplats,
  ),
);

function computeGaussianSplats(
  iElements: IElementsState,
  scene: CurrentScene,
  area: CurrentAreaData,
): IElementGaussianSplats | undefined {
  if (isIElementGaussianSplats(scene.activeElement)) {
    return scene.activeElement;
  }

  // A Rovi scan's splat lives on the DataSetCapture the scan belongs to, so the
  // active waypoint (or reference) can resolve it through that ancestor.
  const roviScanSplats =
    computeRoviScanGaussianSplats(iElements, scene.activeElement) ??
    computeRoviScanGaussianSplats(iElements, scene.referenceElement);
  if (roviScanSplats) {
    return roviScanSplats;
  }

  const reference = scene.referenceElement ?? area.dataSessions.at(0);
  if (!reference) {
    return;
  }

  const state = { iElements };
  const section = pickClosestInTime(reference, [
    ...area.dataSessions3d.gaussianSplats,
  ]);
  return section
    ? selectChildDepthFirst(section, isIElementViewableGaussianSplats)(state)
    : undefined;
}

/**
 * Finds the DataSetCapture a Rovi-scan element belongs to.
 *
 * The Rovi scan's Gaussian splat and point cloud stream are stored as children of
 * the DataSetCapture that contains the scan, which also holds the odometry path
 * whose waypoints reference the element.
 *
 * @param iElements the iElements store slice
 * @param element the element to look up from (e.g. a clicked odometry-path waypoint)
 * @returns The DataSetCapture ancestor, or undefined when the element is not in a Rovi scan
 */
function selectRoviScanDataSetCapture(
  iElements: IElementsState,
  element: IElement | undefined,
): IElement | undefined {
  const state = { iElements };
  const roviScan = selectAncestor(element, isIElementSectionRoviScan)(state);
  if (!roviScan) {
    return;
  }
  return selectAncestor(roviScan, isIElementSectionDataSetCapture)(state);
}

/**
 * Finds the Gaussian splats of the Rovi scan that contains the given element.
 *
 * @param iElements the iElements store slice
 * @param element the element to look up from (e.g. a clicked odometry-path waypoint)
 * @returns The Rovi scan Gaussian splats or undefined if not found
 */
function computeRoviScanGaussianSplats(
  iElements: IElementsState,
  element: IElement | undefined,
): IElementGaussianSplats | undefined {
  const dataSetCapture = selectRoviScanDataSetCapture(iElements, element);
  if (!dataSetCapture) {
    return;
  }
  return selectChildDepthFirst(
    dataSetCapture,
    isIElementViewableGaussianSplats,
  )({ iElements });
}

/**
 * Finds the viewable point cloud stream of the Rovi scan that contains the given element.
 *
 * @param iElements the iElements store slice
 * @param element the element to look up from (e.g. a clicked odometry-path waypoint)
 * @returns The Rovi scan point cloud stream or undefined if not found
 */
function computeRoviScanPointCloud(
  iElements: IElementsState,
  element: IElement | undefined,
): IElementPointCloudStream | undefined {
  return computeSectionPointCloud(
    iElements,
    selectRoviScanDataSetCapture(iElements, element),
  );
}

/**
 * Finds the viewable point cloud stream stored next to the given Gaussian splats.
 *
 * Both are children of the same DataSetCapture, and the point cloud is what the zoom lens
 * lets the user pick on while the splats, which cannot be picked, are displayed.
 *
 * @param iElements the iElements store slice
 * @param splats the Gaussian splats to look up from
 * @returns The point cloud stream of the same DataSetCapture, or undefined when there is none
 */
export function computeDataSetCapturePointCloud(
  iElements: IElementsState,
  splats: IElementGaussianSplats | undefined,
): IElementPointCloudStream | undefined {
  return computeSectionPointCloud(
    iElements,
    selectAncestor(splats, isIElementSectionDataSetCapture)({ iElements }),
  );
}

/**
 * @param iElements the iElements store slice
 * @param section the section to search in
 * @returns The viewable point cloud stream inside the given section, if any
 */
function computeSectionPointCloud(
  iElements: IElementsState,
  section: IElement | undefined,
): IElementPointCloudStream | undefined {
  if (!section) {
    return;
  }
  const state = { iElements };
  return selectChildDepthFirst(section, (el): el is IElementPointCloudStream =>
    selectIsElementViewablePointCloudStream(el)(state),
  )(state);
}

/**
 * Selects the scene filter to apply when a walk-mode waypoint is clicked.
 *
 * For a Rovi scan path waypoint, prefers the Gaussian splats (when the feature is
 * enabled and available), otherwise the point cloud stream (when the user has the
 * point cloud entitlement), otherwise the panorama.
 *
 * @param state The state of the app store
 * @param waypoint The clicked waypoint element
 * @returns The scene filter to activate
 */
export const selectWaypointTargetSceneFilter = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectCanReadPointCloud,
      (state: IElementsSubscriptionsFeatures, waypoint: IElement | undefined) =>
        waypoint,
    ],
    (iElements, canReadPointCloud, waypoint): SceneFilter => {
      if (waypoint?.type !== IElementType.img360) {
        if (computeRoviScanGaussianSplats(iElements, waypoint)) {
          return SceneFilter.GaussianSplats;
        }
        if (
          canReadPointCloud &&
          computeRoviScanPointCloud(iElements, waypoint)
        ) {
          return SceneFilter.PointCloud;
        }
      }
      return SceneFilter.Pano;
    },
  ),
);

/**
 * @param scene current active scene
 * @param cameraPosition position of the camera
 * @param extendedSearch true to extend the search to all image data in the current scene
 * @param activeElement Optional active element to start the search from. Defaults to the current active element in the scene.
 * @returns the closest image to the camera. Prefers 360, but will also return 2d photos or orthomosaics if no 360 is available.
 */
export function selectBestImage(
  scene: CurrentScene,
  cameraPosition: Vector3,
  extendedSearch = false,
  activeElement: IElement | undefined = scene.activeElement,
) {
  return (state: OnlyIElementsStore): WalkSceneActiveElement | undefined => {
    let images: WalkSceneActiveElement[] = selectDataSet360s(
      activeElement,
      true,
      scene.shouldUseIntensityData,
    )(state);
    if (images.length === 0 || extendedSearch) {
      images = [
        ...scene.panos,
        ...scene.paths.flatMap((path) =>
          selectChildrenDepthFirst(path, isIElementImg360)(state),
        ),
      ];
    }

    // If we find no 360 data, also consider other types of images (2d photos, orthomosaics) that are allowed in walk mode
    if (images.length === 1) {
      // This happens whenever the user clicks a precise pano
      return images[0];
    }
    if (images.length === 0) {
      images = [
        ...scene.photos,
        ...scene.orthomosaics
          .map((orthomosaic) =>
            selectChildDepthFirst(orthomosaic, isIElementOrthomosaic)(state),
          )
          .filter(isValid),
      ];
    }

    return selectClosestElementToPosition(images, cameraPosition)(state);
  };
}

/**
 * Given the current scene and area data, compute the new active element desired
 * based on the walk mode active type
 *
 * @param state The state of the app store
 * @param scene The current active scene
 * @param area The data available in the current area
 * @param cameraPosition The current camera position
 * @param newSceneFilter The new desired scene filter
 * @returns The best IElement for the current active type
 */
export const selectWalkSceneElements = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, scene: CurrentScene) => scene,
      (state: OnlyIElementsStore, scene: CurrentScene, area: CurrentAreaData) =>
        area,
      (
        state: OnlyIElementsStore,
        scene: CurrentScene,
        area: CurrentAreaData,
        cameraPosition: Vector3,
      ) => cameraPosition,
      (
        state: OnlyIElementsStore,
        scene: CurrentScene,
        area: CurrentAreaData,
        cameraPosition: Vector3,
        newSceneFilter?: SceneFilter,
      ) => newSceneFilter,
    ],
    computeWalkSceneElements,
  ),
);

function computeWalkSceneElements(
  iElements: IElementsState,
  scene: CurrentScene,
  area: CurrentAreaData,
  cameraPosition: Vector3,
  newSceneFilter?: SceneFilter,
): Array<WalkSceneActiveElement | undefined> {
  const state = { iElements };
  switch (newSceneFilter) {
    case SceneFilter.PointCloud: {
      return [selectComputePointcloud(scene, area)(state)];
    }
    case SceneFilter.Cad: {
      return [scene.cad];
    }
    case SceneFilter.Pano: {
      if (isIElementImg2d(scene.activeElement)) {
        return [scene.activeElement];
      }
      if (
        isIElementPhotogrammetryScan(scene.activeElement) ||
        isIElementOrthomosaic(scene.activeElement)
      ) {
        return [computeOrthoMosaic(state.iElements, scene.activeElement)];
      }

      return [selectBestImage(scene, cameraPosition)(state)];
    }
    case SceneFilter.Overlay: {
      const pointCloud = selectComputePointcloud(scene, area)(state);
      const { cad } = scene;
      return [pointCloud, cad];
    }
    default:
      return [];
  }
}

/**
 * Returns an IElementDepthMap associated to a given IElementImg360.
 *
 * @param state The state of the app store
 * @param pano The pano of which the depth data are queried
 * @returns Depth map associated to the give pano
 */
export const selectDepthMapForImg360 = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, pano: IElementImg360 | undefined) => pano,
    ],
    computeDepthMapForImg360,
  ),
);

function computeDepthMapForImg360(
  iElements: IElementsState,
  pano: IElementImg360 | undefined,
): IElementDepthMap | undefined {
  if (!pano) return;
  if (!pano.parentId) return;
  const state = { iElements };
  const parent = selectIElement(pano.parentId)(state);
  return selectChildDepthFirst(parent, isIElementDepthMap)(state);
}

function computeOrthoMosaic(
  iElements: IElementsState,
  element: IElementOrthomosaic | IElementPhotogrammetryScan,
): IElementOrthomosaic | undefined {
  return selectChildDepthFirst(element, isIElementOrthomosaic)({ iElements });
}

/**
 * Check if the current element is Img360 with depth information
 *
 * @param element The element of which the depth data are queried
 * @returns Depth map associated to the given element
 */
export function selectIsPanoWithDepth(element: IElement | undefined) {
  return (state: OnlyIElementsStore): boolean => {
    if (!element || !isIElementImg360(element)) return false;
    return !!selectDepthMapForImg360(element)(state);
  };
}

/**
 * @returns True if the iElement is part of a room that was promoted from a videomode trajectory
 * @param state The current app state
 * @param iElement The element to check
 */
export const selectIsVideoModeTimePoint = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectVideoModeCopyTag,
      (state: OnlyIElementsStore, iElement: IElement | undefined) => iElement,
    ],
    (iElements, videoModeTag, iElement) => {
      const room = selectAncestor(
        iElement,
        (e) => isIElementRoomSection(e) || isIElementTimeTravelSection(e),
      )({ iElements });
      return room?.labelIds?.some((label) => label === videoModeTag);
    },
  ),
);

/**
 * @returns True if the input element is a panorama image with an accurate 3D orientation
 * @param state The current app state
 * @param iElement The element to check
 */
export const selectIsPanoAligned = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (
        state: OnlyIElementsStore & OnlyFeaturesState,
        iElement: WalkSceneActiveElement | undefined,
      ) => iElement,
    ],
    (iElements, iElement) =>
      !!iElement &&
      isIElementImg360(iElement) &&
      (!!iElement.isRotationAccurate ||
        !!selectIsVideoModeTimePoint(iElement)({ iElements })),
  ),
);

/**
 * @param element data session element containing a point cloud stream to check
 * @returns true if the data session contains an unaligned point cloud, false otherwise
 */
export function selectIsDataSessionWithUnalignedPointCloud(
  element: IElement | undefined,
): (state: State) => boolean {
  return (state: State): boolean =>
    !!element &&
    isIElementSectionDataSession(element) &&
    !!selectChildDepthFirst(
      element,
      isIElementGenericPointCloudStream,
    )(state) &&
    !selectIsPointCloudAligned(element)(state);
}

/**
 * Check if the given element is an aligned element based on its type and ancestors
 *
 * @param state The current app state
 * @param element The element to check
 * @returns True if the input element is an aligned element
 */
export const selectIsElementAligned = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectFeaturesSlice,
      (
        state: OnlyIElementsStore & OnlyFeaturesState,
        element: IElement | undefined,
      ) => element,
    ],
    (iElements, features, element) => {
      if (!element) return false;

      // Get the ancestor
      const parent = selectAncestor(
        element,
        (el) =>
          isIElementGenericDataSession(el) ||
          isIElementImg360(el) ||
          isAreaSection(el),
      )({ iElements });

      if (!parent) return false;

      // Check if the parent is a data session and if it is aligned
      if (isIElementGenericDataSession(parent)) {
        const isDataSessionAligned = selectIsDataSessionAligned(parent)({
          iElements,
        });
        // If the data session is not aligned, check the pointcloud inside of it
        if (!isDataSessionAligned) {
          return selectIsDataSessionWithUnalignedPointCloud(element)({
            iElements,
          });
        }
        return isDataSessionAligned;
      }

      // If the parent is a pano, check if it is aligned
      if (isIElementImg360(parent)) {
        return selectIsPanoAligned(parent)({ iElements, features });
      }

      // If the parent is an area section, check if it is a floor without scale (which is not aligned) or not
      if (isAreaSection(parent)) {
        return !selectIsFloorWithoutScale(parent)({ iElements });
      }

      return false;
    },
  ),
);
